'use client';

import { useEffect, useRef } from 'react';

import { cn } from '../utils/cn';

const DEFAULT_SPEED = 800; // pixels per second

/**
 * Map a 0–1 progress value to (x, y) on a rounded-rect perimeter, clockwise
 * from top-left. Exported for tests only.
 */
export function perimeterPoint(
	t: number,
	w: number,
	h: number,
	radius: number,
): { x: number; y: number } {
	const r = Math.min(radius, w / 2, h / 2);
	const topLen = w - 2 * r;
	const rightLen = h - 2 * r;
	const bottomLen = w - 2 * r;
	const leftLen = h - 2 * r;
	const arc = (Math.PI * r) / 2;
	const P = topLen + rightLen + bottomLen + leftLen + 4 * arc;

	let d = (((t % 1) + 1) % 1) * P;

	// Top edge →
	if (d <= topLen) return { x: r + d, y: 0 };
	d -= topLen;

	// Top-right arc ↘
	if (d <= arc) {
		const a = d / r;
		return { x: w - r + r * Math.sin(a), y: r - r * Math.cos(a) };
	}
	d -= arc;

	// Right edge ↓
	if (d <= rightLen) return { x: w, y: r + d };
	d -= rightLen;

	// Bottom-right arc ↙
	if (d <= arc) {
		const a = d / r;
		return { x: w - r + r * Math.cos(a), y: h - r + r * Math.sin(a) };
	}
	d -= arc;

	// Bottom edge ←
	if (d <= bottomLen) return { x: w - r - d, y: h };
	d -= bottomLen;

	// Bottom-left arc ↖
	if (d <= arc) {
		const a = d / r;
		return { x: r - r * Math.sin(a), y: h - r + r * Math.cos(a) };
	}
	d -= arc;

	// Left edge ↑
	if (d <= leftLen) return { x: 0, y: h - r - d };
	d -= leftLen;

	// Top-left arc ↗
	if (d <= arc) {
		const a = d / r;
		return { x: r - r * Math.cos(a), y: r - r * Math.sin(a) };
	}

	return { x: r, y: 0 };
}

export interface SpinnerBorderProps {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	/** Travel speed in px/s (default 150) */
	speed?: number;
	/**
	 * When `false`, the wrapper + children stay mounted but the animated border
	 * fades out and its rAF loop is parked (zero cost at rest). This lets a parent
	 * toggle the border on submit WITHOUT remounting the wrapped subtree — critical
	 * for the morphing composer, whose child (`AskNaumuBar`) holds in-flight
	 * recording / staged-file state that a remount would destroy mid-send.
	 * Defaults `true` so existing always-on callers are byte-unchanged.
	 */
	active?: boolean;
}

export const SpinnerBorder = ({
	children,
	className,
	style,
	speed = DEFAULT_SPEED,
	active = true,
}: SpinnerBorderProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const innerGlowRef = useRef<HTMLDivElement>(null);
	const thinOutlineRef = useRef<HTMLDivElement>(null);
	const thickOutlineRef = useRef<HTMLDivElement>(null);

	const dims = useRef({ w: 0, h: 0, r: 0 });

	// Measure wrapper and sync outline border-radii
	useEffect(() => {
		const el = wrapperRef.current;
		if (!el) return;

		const measure = () => {
			// Layout size, NOT getBoundingClientRect: the wrapper is routinely
			// mounted inside a scale-in animation (framer-motion `scale: 0.6 → 1`),
			// and a bounding rect taken mid-transform bakes the scaled-down size
			// into the perimeter FOREVER - ResizeObserver never refires because a
			// CSS transform does not change layout size. The glow then travels a
			// smaller rectangle pinned to the top-left and renders as a broken
			// partial arc instead of a full outline.
			const r = parseFloat(getComputedStyle(el).borderRadius) || 0;
			dims.current = { w: el.offsetWidth, h: el.offsetHeight, r };
			if (thinOutlineRef.current)
				thinOutlineRef.current.style.borderRadius = `${r + 1}px`;
			if (thickOutlineRef.current)
				thickOutlineRef.current.style.borderRadius = `${r + 2}px`;
		};

		const observer = new ResizeObserver(measure);
		observer.observe(el);
		measure();
		return () => observer.disconnect();
	}, []);

	// Animation loop — direct DOM updates via refs, no React re-renders. Parked
	// while inactive so an always-mounted (but resting) border costs nothing; the
	// last-painted gradient is simply faded out by the layers' opacity below.
	useEffect(() => {
		if (!active) return;
		let frameId: number;
		const t0 = performance.now();

		const tick = (now: number) => {
			const { w, h, r } = dims.current;
			if (w > 0 && h > 0) {
				const rr = Math.min(r, w / 2, h / 2);
				const P =
					2 * (w - 2 * rr) + 2 * (h - 2 * rr) + 2 * Math.PI * rr;
				const elapsed = (now - t0) / 1000;
				const progress = (elapsed * speed) / P;

				// Compute position on each layer's own perimeter
				// Layer 1 — inner glow: same bounds as wrapper
				const p1 = perimeterPoint(progress, w, h, r);
				if (innerGlowRef.current) {
					innerGlowRef.current.style.background = `radial-gradient(140px circle at ${p1.x}px ${p1.y}px, color-mix(in srgb, var(--color-accent) 18%, transparent), transparent 70%)`;
				}

				// Layer 2 — thin outline: 1px larger on each side
				const p2 = perimeterPoint(progress, w + 2, h + 2, r + 1);
				if (thinOutlineRef.current) {
					thinOutlineRef.current.style.background = `radial-gradient(100px circle at ${p2.x}px ${p2.y}px, var(--color-accent), transparent 70%)`;
				}

				// Layer 3 — thick outline: 2px larger on each side
				const p3 = perimeterPoint(progress, w + 4, h + 4, r + 2);
				if (thickOutlineRef.current) {
					thickOutlineRef.current.style.background = `radial-gradient(60px circle at ${p3.x}px ${p3.y}px, var(--color-accent), transparent 60%)`;
				}
			}

			frameId = requestAnimationFrame(tick);
		};

		frameId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frameId);
	}, [speed, active]);

	const layerOpacity = active ? 1 : 0;

	return (
		<div ref={wrapperRef} className={cn('relative', className)} style={style}>
			{/* Layer 1: Inner glow — no clip so gradient centers on border */}
			<div
				ref={innerGlowRef}
				className="pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-200 ease-out"
				style={{ opacity: layerOpacity }}
			/>

			{/* Layer 2: Thin outline (1px) — masked to border ring */}
			<div
				ref={thinOutlineRef}
				className="pointer-events-none absolute transition-opacity duration-200 ease-out"
				style={{
					opacity: layerOpacity,
					inset: -1,
					padding: 1,
					WebkitMask:
						'linear-gradient(var(--color-neutral) 0 0) content-box, linear-gradient(var(--color-neutral) 0 0)',
					WebkitMaskComposite: 'xor',
					mask: 'linear-gradient(var(--color-neutral) 0 0) content-box, linear-gradient(var(--color-neutral) 0 0)',
					maskComposite: 'exclude',
				}}
			/>

			{/* Layer 3: Thick outline (2px) — masked to border ring */}
			<div
				ref={thickOutlineRef}
				className="pointer-events-none absolute transition-opacity duration-200 ease-out"
				style={{
					opacity: layerOpacity,
					inset: -2,
					padding: 2,
					WebkitMask:
						'linear-gradient(var(--color-neutral) 0 0) content-box, linear-gradient(var(--color-neutral) 0 0)',
					WebkitMaskComposite: 'xor',
					mask: 'linear-gradient(var(--color-neutral) 0 0) content-box, linear-gradient(var(--color-neutral) 0 0)',
					maskComposite: 'exclude',
				}}
			/>

			{children}
		</div>
	);
};
